import type { ReactNode } from 'react';
import { Image } from '@/components';
import {
    PopoverPositioner,
    PopoverRoot,
    PopoverTrigger,
} from '@/components/Popover';
import { cn } from '@/lib/utils';

interface EntityThumbnailWithPreviewProps {
    imageUrl: string | null | undefined;
    name: string;
    fallbackIcon: ReactNode;
    size?: 'sm' | 'md';
}

const SIZE_CLASSES: Record<'sm' | 'md', string> = {
    sm: 'h-10 w-10',
    md: 'h-14 w-14',
};

export function EntityThumbnailWithPreview({
    imageUrl,
    name,
    fallbackIcon,
    size = 'md',
}: EntityThumbnailWithPreviewProps) {
    const sizeClass = SIZE_CLASSES[size];

    const thumbnail = (
        <div
            className={cn(
                'flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-grey-600',
                sizeClass,
            )}
        >
            {imageUrl ? (
                <Image
                    src={imageUrl}
                    alt={name}
                    auth
                    className="h-full w-full object-cover"
                />
            ) : (
                fallbackIcon
            )}
        </div>
    );

    if (!imageUrl) {
        return thumbnail;
    }

    return (
        <PopoverRoot>
            <PopoverTrigger asChild>
                <div
                    role="button"
                    tabIndex={0}
                    aria-label={name}
                    className={cn(
                        'flex shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-md bg-grey-600 outline-none focus-visible:ring-2 focus-visible:ring-grey-400 focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900',
                        sizeClass,
                    )}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.stopPropagation();
                        }
                    }}
                >
                    <Image
                        src={imageUrl}
                        alt={name}
                        auth
                        className="h-full w-full object-cover"
                    />
                </div>
            </PopoverTrigger>
            <PopoverPositioner
                side="right"
                sideOffset={8}
                className="rounded-md border border-grey-500 bg-grey-700 p-1 shadow-md"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="h-52 w-52 overflow-hidden rounded-md">
                    <Image
                        src={imageUrl}
                        alt={name}
                        auth
                        className="h-full w-full object-cover"
                    />
                </div>
            </PopoverPositioner>
        </PopoverRoot>
    );
}
